import { createContext, useEffect, useReducer, useRef } from 'react';
import type { FC, ReactNode } from 'react';
import type { User } from 'src/types/user';
import SplashScreen from 'src/components/SplashScreen';
import { getConnectedApps } from 'src/requests/connectedApps';
import { getProviderInfo } from 'src/requests/provider';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'src/store';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js';
import { useHistory } from 'react-router';
import api, { setAuthToken } from 'src/requests/axiosConfig';
import axios from 'axios';
import { getCurrentSubscriptions } from 'src/requests/subscriptions';

const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
});

interface AuthState {
  isInitialised: boolean;
  isAuthenticated: boolean;
  user: User | null;
}

interface RegisterParams {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  password: string;
  clinicName?: string;
  clinicPhoneNumber?: string;
  clinicAddress?: string;
  clinicUrl?: string;
  clinicServiceDetails?: string;
  userType: string;
  inviteToken?: string;
}

interface AuthContextValue extends AuthState {
  method: 'Cognito';
  login: (email: string, password: string) => Promise<any>;
  logout: () => void;
  completeNewPasswordChallenge: (
    email: string,
    oldPassword: string,
    newPassword: string
  ) => Promise<void>;
  register: (data: RegisterParams) => Promise<any>;
  forgotPassword: (email: string) => Promise<any>;
  resetPassword: (email: string, verificationCode: string, newPassword: string) => Promise<any>;
  updateUser: (user?: User, isNewUser?: boolean) => any;
  changePassword: (oldPassword: string, newPassword: string) => Promise<any>;
  getNewAccessToken: () => Promise<boolean>;
}

interface AuthProviderProps {
  children: ReactNode;
}

type InitialiseAction = {
  type: 'INITIALISE';
  payload: {
    isAuthenticated: boolean;
    user: User | null;
  };
};

type LoginAction = {
  type: 'LOGIN';
  payload: {
    isAuthenticated: boolean;
    user: User;
  };
};

type LogoutAction = {
  type: 'LOGOUT';
};

type RegisterAction = {
  type: 'REGISTER';
  payload: {
    user: User;
  };
};

type UpdateAction = {
  type: 'UPDATE';
  payload: {
    user: User;
  };
};

type Action = InitialiseAction | LoginAction | LogoutAction | RegisterAction | UpdateAction;

const initialAuthState: AuthState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null,
};

const reducer = (state: AuthState, action: Action): AuthState => {
  switch (action.type) {
    case 'INITIALISE': {
      const { isAuthenticated, user } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
      };
    }
    case 'LOGIN': {
      const { isAuthenticated, user } = action.payload;

      return {
        ...state,
        isInitialised: true,
        isAuthenticated,
        user,
      };
    }
    case 'UPDATE': {
      const { user } = action.payload;

      return {
        ...state,
        isInitialised: true,
        isAuthenticated: true,
        user,
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };
    }
    case 'REGISTER': {
      const { user } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        user,
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext<AuthContextValue>({
  ...initialAuthState,
  method: 'Cognito',
  login: () => Promise.resolve(),
  logout: () => {},
  completeNewPasswordChallenge: () => Promise.resolve(),
  register: () => Promise.resolve(),
  forgotPassword: () => Promise.resolve(),
  resetPassword: () => Promise.resolve(),
  updateUser: () => Promise.resolve(),
  changePassword: () => Promise.resolve(),
  getNewAccessToken: () => Promise.resolve(false),
});

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const reduxDispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const cognitoUserRef = useRef<CognitoUser | null>(null);
  const history = useHistory();

  const refreshSession = async () => {
    try {
      const refreshTokenString = localStorage.getItem('refreshToken');
      if (!refreshTokenString) {
        return false;
      }

      const refreshToken = new CognitoRefreshToken({
        RefreshToken: refreshTokenString,
      });

      return new Promise((resolve, reject) => {
        const cognitoUser = userPool.getCurrentUser();

        if (!cognitoUser) {
          cognitoUserRef.current = null;
          resolve(false);
          return;
        }

        cognitoUser.getSession(async (sessionErr, newSession) => {
          if (sessionErr) {
            console.error('GET SESSION FAILED', sessionErr);

            // Try direct refresh instead of failing immediately
            cognitoUser.refreshSession(refreshToken, (refreshErr, refreshedSession) => {
              if (refreshErr) {
                console.error('REFRESH TOKEN FAILED', refreshErr);
                reject(refreshErr);
              } else {
                cognitoUserRef.current = cognitoUser;
                const token = refreshedSession?.getAccessToken()?.getJwtToken();
                const refreshToken = refreshedSession?.getRefreshToken()?.getToken();

                localStorage.setItem('refreshToken', refreshToken);
                localStorage.setItem('accessToken', token);
                setAuthToken(token);
                axios.defaults.headers.common.Authorization = `Bearer ${token}`;

                // Store session explicitly to help Cognito remember state
                cognitoUser.setSignInUserSession(refreshedSession);

                resolve(true);
              }
            });
            return;
          }

          if (newSession && newSession.isValid()) {
            cognitoUserRef.current = cognitoUser;
            const token = newSession?.getAccessToken()?.getJwtToken();
            const refreshToken = newSession?.getRefreshToken()?.getToken();

            localStorage.setItem('refreshToken', refreshToken);
            localStorage.setItem('accessToken', token);
            setAuthToken(token);
            axios.defaults.headers.common.Authorization = `Bearer ${token}`;

            // Store session explicitly to help Cognito remember state
            cognitoUser.setSignInUserSession(newSession);

            resolve(true);
          } else {
            cognitoUser.refreshSession(refreshToken, (refreshErr, refreshedSession) => {
              if (refreshErr) {
                console.error('REFRESH TOKEN FAILED', refreshErr);
                reject(refreshErr);
              } else {
                cognitoUserRef.current = cognitoUser;
                const token = refreshedSession?.getAccessToken()?.getJwtToken();
                const refreshToken = refreshedSession?.getRefreshToken()?.getToken();

                localStorage.setItem('refreshToken', refreshToken);
                localStorage.setItem('accessToken', token);
                setAuthToken(token);
                axios.defaults.headers.common.Authorization = `Bearer ${token}`;

                // Store session explicitly to help Cognito remember state
                cognitoUser.setSignInUserSession(refreshedSession);

                resolve(true);
              }
            });
          }
        });
      });
    } catch (error) {
      console.error('Error refreshing session:', error);
      return false;
    }
  };

  const getNewAccessToken = async (): Promise<boolean> => {
    try {
      const refreshTokenString = localStorage.getItem('refreshToken');
      if (!refreshTokenString) {
        return false;
      }

      const refreshToken = new CognitoRefreshToken({
        RefreshToken: refreshTokenString,
      });

      return new Promise<boolean>((resolve, reject) => {
        const cognitoUser = userPool.getCurrentUser();

        if (!cognitoUser) {
          cognitoUserRef.current = null;
          resolve(false);
          return;
        }

        cognitoUser.refreshSession(refreshToken, (refreshErr, refreshedSession) => {
          if (refreshErr) {
            console.error('REFRESH TOKEN FAILED', refreshErr);
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            cognitoUserRef.current = null;
            reject(refreshErr);
            return;
          }

          if (refreshedSession && refreshedSession.isValid()) {
            const newAccessToken = refreshedSession.getAccessToken().getJwtToken();
            const newRefreshToken = refreshedSession.getRefreshToken().getToken();

            localStorage.setItem('accessToken', newAccessToken);
            localStorage.setItem('refreshToken', newRefreshToken);
            setAuthToken(newAccessToken);
            axios.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;

            cognitoUser.setSignInUserSession(refreshedSession);
            cognitoUserRef.current = cognitoUser;

            resolve(true);
          } else {
            console.error('Refreshed session is invalid');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            cognitoUserRef.current = null;
            resolve(false);
          }
        });
      });
    } catch (error) {
      console.error('Error in refreshSession:', error);
      return false;
    }
  };

  const login = async (email: string, password: string) => {
    const cogDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    cognitoUserRef.current = cognitoUser;

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(cogDetails, {
        onSuccess: async (successSession) => {
          cognitoUserRef.current = cognitoUser;
          const token = successSession?.getAccessToken()?.getJwtToken();
          const refreshToken = successSession?.getRefreshToken()?.getToken();
          const therapistId = successSession?.getIdToken().payload['custom:userId'];
          const organizationId = successSession?.getIdToken().payload['custom:organizationId'];

          localStorage.setItem('userId', therapistId);
          localStorage.setItem('refreshToken', refreshToken);
          localStorage.setItem('accessToken', token);
          localStorage.setItem('organizationId', organizationId);
          setAuthToken(token);

          cognitoUser.setSignInUserSession(successSession);

          const [{ providerInfo, error: providerInfoFail }, { apps, appsNames }, subscriptionData] =
            await Promise.all([
              getProviderInfo(therapistId),
              getConnectedApps(therapistId),
              getCurrentSubscriptions(organizationId),
            ]);

          if (providerInfo?.userType === 'C') {
            resolve({
              reasonCode: 'unacceptable_client_login',
              user: providerInfo,
            });
          } else {
            if (!providerInfoFail) {
              const tempUser: User = providerInfo;
              const userWithApps = {
                ...tempUser,
                subscriptionId: subscriptionData[0]?.subscriptionId,
                productType:
                  subscriptionData[0]?.items?.map((item) => item.product.productType) || [],
                apps,
                appsNames,
              };

              dispatch({
                type: 'LOGIN',
                payload: {
                  isAuthenticated: true,
                  user: userWithApps,
                },
              });

              resolve(null);
            } else {
              reject('error');
              enqueueSnackbar(
                'There has been an error getting your user information, try again or contact support.',
                {
                  variant: 'error',
                }
              );
            }
          }
        },
        onFailure: (err) => {
          console.error('Login failure:', err);
          reject(err);
        },
        newPasswordRequired: (userAttributes) => {
          resolve({
            reasonCode: 'new_password_required',
            userAttributes,
          });
        },
      });
    });
  };

  const logout = () => {
    history.push('/');
    localStorage.clear();
    delete axios.defaults.headers.common.Authorization;
    dispatch({ type: 'LOGOUT' });
    reduxDispatch({ type: 'LOGOUT' });

    if (cognitoUserRef.current) {
      cognitoUserRef.current.signOut();
      cognitoUserRef.current = null;
    }
  };

  const register = async (data: any) => {
    try {
      const res = await axios({
        method: 'post',
        url: `/v2/register`,
        data: {
          ...data,
        },
        headers: { 'X-Skip-Auth': 'true' },
      });

      dispatch({
        type: 'REGISTER',
        payload: {
          user: res.data.customer,
        },
      });

      return true;
    } catch (e) {
      console.error(e);
      enqueueSnackbar(e.response.data, {
        variant: 'error',
      });
      return false;
    }
  };

  const forgotPassword = async (email: string) => {
    return new Promise<{ error: boolean; data?: string }>((resolve, reject) => {
      try {
        const cognitoUser = new CognitoUser({
          Username: email.trim().toLowerCase(),
          Pool: userPool,
        });

        cognitoUser.forgotPassword({
          onSuccess: () => {
            resolve({ error: false });
          },
          onFailure: (err) => {
            resolve({ error: true, data: err.message });
          },
          inputVerificationCode: () => {
            resolve({ error: false });
          },
        });
      } catch (error) {
        resolve({
          error: true,
          data:
            error instanceof Error
              ? error.message
              : 'Unknown error during password reset, try again later or contact support.',
        });
      }
    });
  };

  const resetPassword = async (email: string, verificationCode: string, newPassword: string) => {
    return new Promise<any>((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });

      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess() {
          resolve({ error: false });
        },
        onFailure(err) {
          reject({ error: true, data: err.message });
        },
      });
    });
  };

  const changePassword = async (oldPassword: string, newPassword: string) => {
    return new Promise<any>((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();

      if (!cognitoUser) {
        reject(new Error('No user is currently logged in'));
        return;
      }

      cognitoUser.getSession(async (err: Error | null, session: any) => {
        if (err) {
          console.error('Error getting session during password change:', err);

          try {
            const refreshSuccessful = await refreshSession();
            if (!refreshSuccessful) {
              reject({ error: true, data: 'Failed to refresh session' });
              return;
            }
          } catch (refreshErr) {
            reject({ error: true, data: 'Failed to refresh session' });
            return;
          }
        }

        cognitoUser.changePassword(oldPassword, newPassword, (changeErr, result) => {
          if (changeErr) {
            console.error('Error changing password:', changeErr);
            enqueueSnackbar(`Error changing password, ${changeErr.message}`, {
              variant: 'error',
            });
            reject({ error: true, data: changeErr.message });
            return;
          }

          enqueueSnackbar('Password changed successfully', {
            variant: 'success',
          });
          resolve({ error: false });
        });
      });
    });
  };

  const updateUser = async (user?: User, isNewUser?: boolean) => {
    const userId = localStorage.getItem('userId');
    const orgId = localStorage.getItem('organizationId');

    try {
      if (user) {
        await api({
          method: 'patch',
          url: `/v2/user/${userId}`,
          data: user,
        });
      }

      const [
        { providerInfo, error: providerInfoFail },
        { apps, appsNames, error },
        subscriptionData,
      ] = await Promise.all([
        getProviderInfo(userId),
        getConnectedApps(userId),
        getCurrentSubscriptions(orgId),
      ]);

      if (providerInfoFail) {
        enqueueSnackbar('Could not get the user info.', {
          variant: 'error',
        });
        return;
      }
      if (error) {
        enqueueSnackbar('Could not get connected apps.', {
          variant: 'error',
        });
        return;
      }

      const tempUser: User = providerInfo;
      const userWithApps = {
        ...tempUser,
        subscriptionId: subscriptionData[0]?.subscriptionId,
        productType: subscriptionData[0]?.items?.map((item) => item.product.productType) || [],
        apps,
        appsNames,
      };

      if (isNewUser) {
        userWithApps.account.subscription.status = 'active';
        userWithApps.account.maxActiveClients = userWithApps.account.maxActiveClients || 5;
      }

      dispatch({
        type: 'UPDATE',
        payload: {
          user: userWithApps,
        },
      });

      return true;
    } catch (error) {
      console.error('Error getting user info:', error);
      enqueueSnackbar('Failed to update user information.', {
        variant: 'error',
      });
      return false;
    }
  };

  const completeNewPasswordChallenge = async (
    email: string,
    oldPassword: string,
    newPassword: string
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        const cogDetails = new AuthenticationDetails({
          Username: email,
          Password: oldPassword,
        });

        const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });

        cognitoUser.authenticateUser(cogDetails, {
          onSuccess: () => {
            // This shouldn't happen in a new password flow
            reject(new Error('Unexpected success response'));
          },
          onFailure: (err) => {
            reject(err);
          },
          newPasswordRequired: (userAttributes) => {
            // Create a new object with only the mutable attributes
            const mutableAttributes: any = {};

            // Only copy attributes that can be modified
            for (const key in userAttributes) {
              // Skip email, phone numbers, and all verified attributes
              if (
                key !== 'email' &&
                key !== 'phone_number' &&
                !key.endsWith('_verified') &&
                key !== 'sub' &&
                key !== 'user_status'
              ) {
                mutableAttributes[key] = userAttributes[key];
              }
            }

            // Remove explicitly the email attribute to prevent any accidental modification
            delete userAttributes.email;

            // Complete the new password challenge with filtered attributes
            cognitoUser.completeNewPasswordChallenge(newPassword, mutableAttributes, {
              onSuccess: async (session) => {
                cognitoUserRef.current = cognitoUser;
                const token = session?.getAccessToken()?.getJwtToken();
                const refreshToken = session?.getRefreshToken()?.getToken();
                const therapistId = session?.getIdToken().payload['custom:userId'];
                const organizationId = session?.getIdToken().payload['custom:organizationId'];

                localStorage.setItem('userId', therapistId);
                localStorage.setItem('refreshToken', refreshToken);
                localStorage.setItem('accessToken', token);
                localStorage.setItem('organizationId', organizationId);
                setAuthToken(token);

                cognitoUser.setSignInUserSession(session);

                const [
                  { providerInfo, error: providerInfoFail },
                  { apps, appsNames, error },
                  subscriptionData,
                ] = await Promise.all([
                  getProviderInfo(therapistId),
                  getConnectedApps(therapistId),
                  getCurrentSubscriptions(organizationId),
                ]);

                if (!providerInfoFail || !error) {
                  const user: User = providerInfo;
                  const userWithApps = {
                    ...user,
                    subscriptionId: subscriptionData?.[0]?.subscriptionId,
                    productType:
                      subscriptionData?.[0]?.items?.map((item) => item.product.productType) || [],
                    apps,
                    appsNames,
                  };

                  dispatch({
                    type: 'LOGIN',
                    payload: {
                      isAuthenticated: true,
                      user: userWithApps,
                    },
                  });

                  resolve(null);
                }
              },
              onFailure: (err) => {
                reject(err);
              },
            });
          },
        });
      } catch (err) {
        reject(err);
      }
    });
  };

  // refresh token before it expires
  useEffect(() => {
    if (state.isAuthenticated) {
      const tokenRefreshInterval = setInterval(
        async () => {
          try {
            await refreshSession();
          } catch (error) {
            console.error('Auto-refresh failed:', error);
            logout();
          }
        },
        55 * 60 * 1000
      );

      return () => clearInterval(tokenRefreshInterval);
    }
  }, [state.isAuthenticated]);

  useEffect(() => {
    const initialise = async () => {
      const isImpersonating = localStorage.getItem('isImpersonating') === 'true';

      if (isImpersonating) {
        const userData = JSON.parse(localStorage.getItem('user'));
        const orgId = userData?.organization?.organizationId;

        try {
          const [{ providerInfo }, { apps, appsNames }, subscriptionData] = await Promise.all([
            getProviderInfo(userData?.userId),
            getConnectedApps(userData?.userId),
            getCurrentSubscriptions(orgId),
          ]);

          const tempUser: User = providerInfo;
          const userWithApps = {
            ...tempUser,
            subscriptionId: subscriptionData?.[0]?.subscriptionId,
            productType:
              subscriptionData?.[0]?.items?.map((item) => item.product.productType) || [],
            apps,
            appsNames,
          };

          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated: true,
              user: userWithApps,
            },
          });
        } catch (error) {
          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated: true,
              user: userData,
            },
          });
        } finally {
          return;
        }
      }

      try {
        const cognitoUser = userPool.getCurrentUser();

        if (cognitoUser) {
          const refreshSuccessful = await refreshSession();

          if (refreshSuccessful) {
            const userId = localStorage.getItem('userId');
            const orgId = localStorage.getItem('organizationId');

            if (userId) {
              try {
                const [
                  { providerInfo, error: providerInfoFail },
                  { apps, appsNames, error },
                  subscriptionData,
                ] = await Promise.all([
                  getProviderInfo(userId),
                  getConnectedApps(userId),
                  getCurrentSubscriptions(orgId),
                ]);

                if (providerInfoFail) {
                  throw new Error('Could not get the user info.');
                }

                if (error) {
                  throw new Error('Could not get connected apps.');
                }

                const tempUser: User = providerInfo;
                const userWithApps = {
                  ...tempUser,
                  subscriptionId: subscriptionData?.[0]?.subscriptionId,
                  productType:
                    subscriptionData?.[0]?.items?.map((item) => item.product.productType) || [],
                  apps,
                  appsNames,
                };

                dispatch({
                  type: 'INITIALISE',
                  payload: {
                    isAuthenticated: true,
                    user: userWithApps,
                  },
                });

                return;
              } catch (apiError) {
                console.error('Error loading user data:', apiError);

                dispatch({
                  type: 'INITIALISE',
                  payload: {
                    isAuthenticated: true,
                    user: { id: userId } as unknown as User,
                  },
                });

                setTimeout(() => {
                  updateUser();
                }, 2000);

                return;
              }
            }
          }
        }

        dispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      } catch (err) {
        console.error('Authentication initialization error:', err);
        dispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialise();
  }, []);

  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'Cognito',
        login,
        logout,
        completeNewPasswordChallenge,
        register,
        forgotPassword,
        resetPassword,
        updateUser,
        changePassword,
        getNewAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
